/**
 * studio/src/router.ts
 *
 * Vue Router 4 with auth guard.
 * Guards: any route with meta.requiresAuth redirects to /auth if not logged in.
 */

import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "./lib/auth";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "dashboard",
      component: () => import("./views/Dashboard.vue"),
      meta: { requiresAuth: true },
    },
    {
      // Shell view: tab between the guided wizard (default) and the
      // legacy fast form. The mode is persisted in localStorage via
      // `usePitchNewMode`. Both children are async-imported so the
      // route's first paint isn't blocked.
      path: "/pitches/new",
      name: "pitch-new",
      component: () => import("./views/PitchNew.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id",
      name: "pitch-edit",
      component: () => import("./views/PitchEditShell.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id/replace",
      name: "pitch-replace",
      component: () => import("./views/PitchReplace.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id/promote",
      name: "pitch-promote",
      component: () => import("./views/PitchPromote.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/pitches/:id/preview",
      name: "pitch-preview",
      component: () => import("./views/PitchPreview.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/billing",
      name: "billing",
      component: () => import("./views/Billing.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/settings",
      name: "settings",
      component: () => import("./views/Settings.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/agents/activity",
      name: "agents-activity",
      component: () => import("./views/AgentsActivity.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/auth",
      name: "auth",
      component: () => import("./views/Auth.vue"),
    },
    {
      // Magic-link callback — reads ?userId=...&secret=... from URL
      path: "/auth/callback",
      name: "auth-callback",
      component: () => import("./views/AuthCallback.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/",
    },
  ],
});

// ─── Navigation guard ────────────────────────────────────────────────────
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Initialize auth state on first navigation
  if (!auth.initialized) {
    await auth.init();
  }

  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return { name: "auth", query: { redirect: to.fullPath } };
  }
});

export default router;
